import {media} from '@nfq/react-grid';

import {isDefined, isDefinedAndPopulated} from 'GlobalShared/utils/typeGuards';
// eslint-disable-next-line @nfq/hexagonal-dependency-direction
import {breakpoints} from 'UI/utils/globalStyles';

import type {Media} from 'types/payload-types';

/**
 * A function that generates a `background-image` CSS property value with the `image-set` syntax, based on the available image sizes for a given breakpoint.
 * It checks if sizes for the given breakpoint and its fallback are defined and constructs the appropriate `image-set` CSS rule with proper type declarations for browser optimization.
 * If no sizes are available for the specified breakpoint, it falls back to the original image URL, and if no image data exists at all, it returns `undefined`.
 *
 * @param image      The media object containing image sizes and mime types from Payload CMS.
 * @param breakpoint The breakpoint for which the image sizes are being retrieved. Valid values are 'lg', 'md', 'sm', 'xl', and 'xs'.
 * @param appendix   Optional additional CSS value to append to the background-image property (e.g., gradients or fallback images).
 * @returns A `background-image` CSS property value with the `image-set` syntax, or `undefined` if no sizes are available.
 *
 * @example
 * ```tsx
 * const imageSetStyle = getImageSet(image, 'lg');
 * const imageWithGradient = getImageSet(image, 'md', 'linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3))');
 * ```
 */
const getImageSet = (image: Media, breakpoint: 'lg' | 'md' | 'sm' | 'xl' | 'xs', appendix?: string) => {
    if (isDefined(image.sizes)) {
        const hasNormal = isDefined(image.sizes[breakpoint]?.url);
        const hasFallback = isDefined(image.sizes[`fallback-${breakpoint}`]?.url);
        const hasNothing = !hasNormal && !hasFallback;

        return `background-image: ${appendix ? `${appendix}, ` : ''}image-set(
        ${hasNothing ? `url(${image.url}) type("${image.mimeType}")` : ''}
        ${hasNormal ? `url(${image.sizes[breakpoint]?.url}) type("${image.sizes[breakpoint]?.mimeType}")${hasFallback ? ',' : ''}` : ''}
        ${hasFallback ? `url(${image.sizes[`fallback-${breakpoint}`]?.url}) type("${image.sizes[`fallback-${breakpoint}`]?.mimeType}")` : ''}
    );`;
    }

    return undefined;
};

/**
 * Generates responsive CSS rules with `image-set()` background images for all breakpoints based on a Payload CMS media object.
 * This function creates a comprehensive set of CSS media queries that provide optimized background images for different screen sizes and device capabilities.
 * It iterates through all defined breakpoints and generates appropriate `image-set()` declarations, allowing browsers to automatically select the best image variant based on resolution and format support.
 *
 * @param image    A Payload media object, string ID, or `null`/`undefined`. Only populated media objects with sizes are processed.
 * @param appendix Optional additional CSS value to append to each background-image property (e.g., gradients or fallback images).
 * @returns A string containing complete CSS rules with media queries and `image-set()` declarations, or an empty string if no image data is available.
 *
 * @example
 * ```tsx
 * const backgroundCSS = generateBgImage(media);
 * const backgroundWithGradient = generateBgImage(media, 'linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3))');
 * ```
 */
export const generateBgImage = (image: Media | string | null | undefined, appendix?: string) => {
    let cssString = '';

    if (isDefinedAndPopulated(image)) {
        if (isDefined(image.sizes)) {
            (breakpoints as unknown as ('lg' | 'md' | 'sm' | 'xl' | 'xs')[]).forEach(breakpoint => {
                if (breakpoint === 'xs') {
                    cssString += `${getImageSet(image, 'xs', appendix)}\n\n`;
                } else if (isDefined(image.sizes?.[breakpoint]?.url) || isDefined(image.sizes?.[`fallback-${breakpoint}`]?.url)) {
                    cssString += `${media(breakpoint)} {
                        ${getImageSet(image, breakpoint, appendix)}\n
                }\n\n`;
                }
            });
        }
    }

    return cssString;
};